import React from 'react';
import useLeverageSliderEngine from 'src/components/TradeForm/LeverageSliderInput/hooks/useLeverageSliderEngine';
import Slider from 'src/components/primitives/Slider/Slider';

interface Props {
	currentLeverage: number;
	maxLeverage: number;
	targetTradeQuoteSize: number;
	totalCurrentPositionSize: number;
	maxQuoteSize: number;
	targetMarketCurrentPositionSize: number;
	sameSide: boolean;
	onCurrentSizeChange: (quoteSize: number) => void;
	onMaxLeverageClick: () => void;
	onLeverageChange: () => void;
	disabled?: boolean;
	dataTestId?: string;
}

const PerpLeverageSliderInput = ({
	currentLeverage,
	maxLeverage,
	targetTradeQuoteSize,
	totalCurrentPositionSize,
	targetMarketCurrentPositionSize: targetCurrentPositionSize,
	maxQuoteSize,
	sameSide,
	onCurrentSizeChange,
	onMaxLeverageClick,
	onLeverageChange,
	dataTestId,
}: Props) => {
	const engineParams = {
		totalCurrentPosition: totalCurrentPositionSize,
		currentLeverage: currentLeverage,
		maxQuote: maxQuoteSize,
		currentPositionQuote: targetCurrentPositionSize,
		maxLeverage,
		currentQuote: targetTradeQuoteSize,
		sameSide,
	};

	const engineValues = useLeverageSliderEngine(engineParams);

	const handleSliderChange = (sliderPct: number) => {
		if (maxQuoteSize === 0) {
			return;
		}

		// Check if this is a 100% leverage selection.
		if (sliderPct === 100 && onMaxLeverageClick) {
			onMaxLeverageClick();
			// Don't call `onCurrentSizeChange` for 100%. Let `usePinTradeformSizeIfMaxLeverageSelected` handle it.
			return;
		} else if (sliderPct < 100 && onLeverageChange) {
			// Any other leverage change (not 100%) should reset maxLeverageSelected to false.
			onLeverageChange();
		}

		// Calculate the leverage value from the slider percentage.
		const amount = engineValues.availableLeveragePoints * (sliderPct / 100);

		const newQuote = engineValues.calcNewQuoteFromLeveragePoint({
			newPt: amount,
		});

		onCurrentSizeChange(newQuote);
	};

	const showDefault = targetTradeQuoteSize <= 0;

	// Get slider value from 0 to 100.
	const percentage = Math.abs(
		(engineValues.currentLeveragePoint / engineValues.availableLeveragePoints) *
			100
	);
	// Round it so the slider doesn't update due to max collateral fluctuations.
	const sliderValue = Math.round(showDefault ? 0 : Math.min(percentage, 100));

	const value = Math.min(Math.max(0, sliderValue), 100);

	return (
		<Slider
			value={value}
			onValueChange={handleSliderChange}
			onValueCommit={handleSliderChange}
			dataTestId={dataTestId}
		/>
	);
};

export default React.memo(PerpLeverageSliderInput);
